import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FiBriefcase, FiPlus, FiUpload, FiMapPin, FiArrowRight, FiCalendar, FiArrowLeft } from "react-icons/fi";
import AppShell from "../components/layout/AppShell";
import { AuthContext } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import {
  Button,
  Input,
  Textarea,
  Modal,
  SearchInput,
  EmptyState,
  Badge,
} from "../components/ui";
import JobCard from "../components/common/JobCard";
import { getJobs, createJob, updateJob, deleteJob } from "../api/jobs";
import { applyToJob, getMyApplications } from "../api/applications";

export default function Jobs() {
  const { user, isAdmin } = useContext(AuthContext);
  const { notify } = useToast();
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  const highlightRef = useRef(null);

  const [jobs, setJobs] = useState([]);
  const [myApplications, setMyApplications] = useState({}); // jobId -> { applicationId, status }
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("all");

  // One modal, two steps — clicking a job always opens on the "details"
  // step first, showing the full posting. Applying only ever happens after
  // explicitly continuing from there, so there's always a way to actually
  // read what a job says before (or instead of) applying to it.
  const [viewJob, setViewJob] = useState(null);
  const [modalStep, setModalStep] = useState("details"); // "details" | "form"
  const [applyForm, setApplyForm] = useState({ name: "", email: "", phone: "" });
  const [resume, setResume] = useState(null);
  const [coverLetter, setCoverLetter] = useState(null);
  const [applying, setApplying] = useState(false);

  const [manageJob, setManageJob] = useState(null);
  const [jobForm, setJobForm] = useState({ title: "", company: "", location: "", description: "", deadline: "" });
  const [savingJob, setSavingJob] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.allSettled([getJobs(), getMyApplications()]).then(([jobsRes, appsRes]) => {
      if (jobsRes.status === "fulfilled") {
        setJobs(jobsRes.value.data?.data || []);
      } else {
        notify("Couldn't load jobs.", "error");
      }
      if (appsRes.status === "fulfilled") {
        setMyApplications(appsRes.value.data?.applications || {});
      }
      setLoading(false);
    });
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-linked from a notification (e.g. "shortlisted for X") — scroll to
  // and highlight the specific card once the list has loaded.
  useEffect(() => {
    if (!loading && highlightId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [loading, highlightId]);

  const locations = useMemo(
    () => Array.from(new Set(jobs.map((j) => j.location).filter(Boolean))),
    [jobs]
  );

  const filtered = useMemo(() => {
    return jobs.filter((j) => {
      const matchesQuery =
        !query.trim() ||
        j.title?.toLowerCase().includes(query.toLowerCase()) ||
        j.company?.toLowerCase().includes(query.toLowerCase());
      const matchesLocation = location === "all" || j.location === location;
      return matchesQuery && matchesLocation;
    });
  }, [jobs, query, location]);

  const isPastDeadline = (job) => job.deadline && new Date(job.deadline) < new Date().setHours(0, 0, 0, 0);

  const openJob = (job) => {
    setViewJob(job);
    setModalStep("details");
    setApplyForm({ name: `${user?.name || ""} ${user?.lastName || ""}`.trim(), email: user?.email || "", phone: "" });
    setResume(null);
    setCoverLetter(null);
  };

  const closeJobModal = () => {
    setViewJob(null);
    setModalStep("details");
  };

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB, matches the backend limit

  const submitApplication = async () => {
    if (!applyForm.name || !applyForm.email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(applyForm.email.trim())) {
      notify("Please enter a valid email address.", "error");
      return;
    }
    if (!/^\+?[1-9]\d{6,14}$/.test(applyForm.phone.replace(/[\s()-]/g, ""))) {
      notify("Please enter a valid phone number.", "error");
      return;
    }
    if (resume && resume.size > MAX_FILE_SIZE) {
      notify("Resume must be a PDF under 5MB.", "error");
      return;
    }
    if (coverLetter && coverLetter.size > MAX_FILE_SIZE) {
      notify("Cover letter must be a PDF under 5MB.", "error");
      return;
    }
    setApplying(true);
    try {
      const formData = new FormData();
      formData.append("name", applyForm.name);
      formData.append("email", applyForm.email);
      formData.append("phone", applyForm.phone);
      if (resume) formData.append("resume", resume);
      if (coverLetter) formData.append("coverLetter", coverLetter);
      await applyToJob(viewJob.id, formData);
      notify(`Application sent for ${viewJob.title}.`, "success");
      setMyApplications((prev) => ({ ...prev, [viewJob.id]: { status: "pending" } }));
      closeJobModal();
    } catch (error) {
      const message = error.response?.status === 409
        ? "You've already applied to this job."
        : error.response?.data?.error || "Couldn't submit your application. Please try again.";
      notify(message, "error");
      if (error.response?.status === 409) closeJobModal();
    } finally {
      setApplying(false);
    }
  };

  const openManage = (job) => {
    setManageJob(job || "new");
    setJobForm(
      job
        ? { title: job.title, company: job.company, location: job.location || "", description: job.description || "", deadline: job.deadline || "" }
        : { title: "", company: "", location: "", description: "", deadline: "" }
    );
  };

  const saveJob = async () => {
    if (!jobForm.title.trim() || !jobForm.company.trim()) return;
    setSavingJob(true);
    try {
      if (manageJob && manageJob !== "new") {
        await updateJob(manageJob.id, jobForm);
        notify("Job updated.", "success");
      } else {
        await createJob(jobForm);
        notify("Job posted.", "success");
      }
      setManageJob(null);
      load();
    } catch (error) {
      notify(error.response?.data?.message || "Couldn't save this job.", "error");
    } finally {
      setSavingJob(false);
    }
  };

  const removeJob = async () => {
    const ok = await confirm({ title: "Delete this job posting?", description: "This job and every application submitted to it will be permanently deleted. This cannot be undone." });
    if (!ok) return;
    setSavingJob(true);
    try {
      await deleteJob(manageJob.id);
      notify("Job removed.", "success");
      setManageJob(null);
      load();
    } catch (error) {
      notify("Couldn't delete this job.", "error");
    } finally {
      setSavingJob(false);
    }
  };

  const viewJobStatus = viewJob ? myApplications[viewJob.id]?.status : null;
  const viewJobStatusConfig = viewJobStatus
    ? { pending: "Applied", shortlisted: "Shortlisted", accepted: "Accepted", rejected: "Not selected" }[viewJobStatus]
    : null;
  const viewJobClosed = viewJob ? isPastDeadline(viewJob) : false;
  const canApply = viewJob && !viewJobStatusConfig && !viewJobClosed;

  return (
    <AppShell title="Jobs" subtitle="Opportunities curated for the Empact network" wide>
      {/* Sticky search + filters — stays reachable while scrolling a long
          list of cards, tucked just under the topbar (h-16) rather than
          overlapping it. */}
      <div className="sticky top-16 z-10 -mx-4 mb-6 border-b border-slate-100 bg-slate-50/95 px-4 py-4 backdrop-blur sm:-mx-6 sm:px-6 lg:mx-0 lg:rounded-xl lg:border lg:px-5 lg:shadow-xs">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-1 flex-col gap-3 sm:flex-row">
            <SearchInput value={query} onChange={setQuery} placeholder="Search job title or company..." className="sm:max-w-xs" />
            {locations.length > 0 && (
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="all">All locations</option>
                {locations.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            )}
          </div>
          {isAdmin && (
            <Button onClick={() => openManage(null)}>
              <FiPlus className="h-4 w-4" /> Post a job
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-slate-100 bg-white p-5">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-slate-100" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-2/3 animate-pulse rounded bg-slate-100" />
                  <div className="h-3 w-1/3 animate-pulse rounded bg-slate-100" />
                </div>
              </div>
              <div className="mt-4 h-3 w-1/2 animate-pulse rounded bg-slate-100" />
              <div className="mt-4 h-9 animate-pulse rounded-lg bg-slate-100" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<FiBriefcase className="h-6 w-6" />}
          title="No jobs match right now"
          description="Check back soon, or try a different search."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {filtered.map((job) => {
            const isHighlighted = String(job.id) === highlightId;
            return (
              <div
                key={job.id}
                ref={isHighlighted ? highlightRef : undefined}
                className={isHighlighted ? "rounded-xl ring-2 ring-primary-300" : undefined}
              >
                <JobCard
                  job={job}
                  status={myApplications[job.id]?.status}
                  isAdmin={isAdmin}
                  onView={openJob}
                  onManage={openManage}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Job details + apply modal — one modal, two steps. Opening always
          lands on "details" so the full posting is visible before anyone
          reaches the application form, whether they clicked the card or
          its "Apply now" button, or their own application's status. */}
      <Modal
        open={Boolean(viewJob)}
        onClose={closeJobModal}
        title={modalStep === "details" ? viewJob?.title : `Apply to ${viewJob?.title || ""}`}
        description={viewJob?.company}
        footer={
          modalStep === "details" ? (
            <>
              <Button variant="secondary" onClick={closeJobModal}>Close</Button>
              {canApply && (
                <Button onClick={() => setModalStep("form")}>
                  Continue to application <FiArrowRight className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setModalStep("details")} disabled={applying}>
                <FiArrowLeft className="h-3.5 w-3.5" /> Back
              </Button>
              <Button onClick={submitApplication} loading={applying}>
                Submit application
              </Button>
            </>
          )
        }
      >
        {modalStep === "details" ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              {viewJob?.location && (
                <Badge variant="slate"><FiMapPin className="h-3 w-3" /> {viewJob.location}</Badge>
              )}
              {viewJob?.deadline && (
                <Badge variant={viewJobClosed ? "red" : "slate"}>
                  <FiCalendar className="h-3 w-3" />
                  {viewJobClosed ? "Closed " : "Apply by "}
                  {new Date(viewJob.deadline).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </Badge>
              )}
              {viewJobStatusConfig && <Badge variant="primary">{viewJobStatusConfig}</Badge>}
            </div>
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Description</p>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                {viewJob?.description || "No description provided."}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Input
              label="Full name"
              value={applyForm.name}
              onChange={(e) => setApplyForm({ ...applyForm, name: e.target.value })}
            />
            <Input
              label="Email address"
              type="email"
              value={applyForm.email}
              onChange={(e) => setApplyForm({ ...applyForm, email: e.target.value })}
            />
            <Input
              label="Phone number"
              value={applyForm.phone}
              onChange={(e) => setApplyForm({ ...applyForm, phone: e.target.value })}
            />
            <FileField label="Resume (PDF, max 5MB)" file={resume} onChange={setResume} accept=".pdf,application/pdf" />
            <FileField label="Cover letter (PDF, max 5MB)" file={coverLetter} onChange={setCoverLetter} accept=".pdf,application/pdf" />
          </div>
        )}
      </Modal>

      {/* Admin manage modal */}
      <Modal
        open={Boolean(manageJob)}
        onClose={() => setManageJob(null)}
        title={manageJob === "new" ? "Post a new job" : "Edit job"}
        footer={
          <>
            {manageJob && manageJob !== "new" && (
              <Button variant="outlineDanger" onClick={removeJob} disabled={savingJob} className="mr-auto">
                Delete
              </Button>
            )}
            <Button variant="secondary" onClick={() => setManageJob(null)} disabled={savingJob}>
              Cancel
            </Button>
            <Button onClick={saveJob} loading={savingJob}>
              Save job
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Job title" value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} />
          <Input label="Company" value={jobForm.company} onChange={(e) => setJobForm({ ...jobForm, company: e.target.value })} />
          <Input label="Location" value={jobForm.location} onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })} />
          <Textarea label="Description" rows={4} value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} />
          <Input
            label="Application deadline (optional)"
            type="date"
            value={jobForm.deadline || ""}
            onChange={(e) => setJobForm({ ...jobForm, deadline: e.target.value })}
          />
        </div>
      </Modal>
    </AppShell>
  );
}

function FileField({ label, file, onChange, accept }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <label className="flex h-11 cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 text-sm text-slate-500 hover:border-primary-300 hover:text-primary-600">
        <FiUpload className="h-4 w-4" />
        {file ? file.name : "Click to upload"}
        <input type="file" accept={accept} className="hidden" onChange={(e) => onChange(e.target.files?.[0] || null)} />
      </label>
    </div>
  );
}
